import { PreviousUtrv } from '@features/assistant';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { AdditionalContext, AIResponse } from '@g17eco/types/ai';
import { UniversalTrackerValuePlain } from '../types/universalTrackerValue';
import { AIAutoAnswerSurveyJobPlain, CreatedJob } from '@g17eco/types/ai-auto-answer-job';
import { BackgroundJob } from '@g17eco/types/background-jobs';

interface GetAIUtrvAssistantParams {
  utrvId: string;
  initiativeId: string;
  additionalContext?: AdditionalContext;
}

export interface AIUtrvSuggestion {
  predictedAnswer?: string | number | { [key: string]: string | number | string[] };
  questionExplanation: string;
  previousUtrvs: PreviousUtrv[];
  targetValue?: number;
  bestPractice: string[];
  keyInfo: string[];
  suggestedEvidence: {
    primaryDocumentation: string[];
    supportingDocumentation: string[];
  };
  whereToFind: {
    externalSource: string[];
    internalSource: string[];
  };
}

interface GetAIUtrvFurtherNotesDraftParams {
  initiativeId: string;
  utrvId: string;
  draftData: Pick<UniversalTrackerValuePlain, 'value' | 'unit' | 'numberScale' | 'valueData'>;
}

export enum RefineAction {
  Rewrite = 'rewrite',
  Shorten = 'shorten',
  AddContext = 'add_context',
}

interface GetAIUtrvFurtherNotesDraftRefineParams extends GetAIUtrvFurtherNotesDraftParams {
  textToRefine: string;
  action: RefineAction;
  additionalContext: string | undefined;
}

interface GetAIAutoAnswerSurveyParams {
  surveyId: string;
  initiativeId: string;
}

export enum SettingOption {
  DocumentLibrary = 'documentIds',
  OverwriteMetric = 'isOverwriteMetric',
  AIAgentMode = 'isAIAgentMode',
  AIAgentModeOptions = 'aIAgentModeOptions',
}
export enum AIModel {
  OpenAI_4o_Mini = 'o4-mini',
  OpenAI_4o = 'gpt-4o',
  OpenAI_4_1_Mini = 'gpt-4.1-mini',
  OpenAI_4_1 = 'gpt-4.1',
  OpenAI_5_Mini = 'gpt-5-mini',
  OpenAI_5 = 'gpt-5',
  OpenAI_5_1 = 'gpt-5.1',
  ClaudeAI_4_5_Sonnet = 'claude-sonnet-4-5',
  ClaudeAI_4_5_Haiku = 'claude-haiku-4-5',
  ClaudeAI_4_5_Opus = 'claude-opus-4-5',
  Gemini_3_Pro = 'gemini-3-pro',
  Gemini_2_5_Flash = 'gemini-2.5-flash',
  Snowflake = 'snowflake', // Not a code
}

export const getAIModelName = (model: AIModel | string) => {
  switch (model) {
    case AIModel.ClaudeAI_4_5_Haiku:
      return 'Claude 4.5 Haiku';
    case AIModel.ClaudeAI_4_5_Opus:
      return 'Claude 4.5 Opus';
    case AIModel.ClaudeAI_4_5_Sonnet:
      return 'Claude 4.5 Sonnet';
    case AIModel.Gemini_3_Pro:
      return 'Gemini 3 Pro';
    case AIModel.Gemini_2_5_Flash:
      return 'Gemini 2.5 Flash';
    case AIModel.OpenAI_4o_Mini:
      return 'GPT-4o Mini';
    case AIModel.OpenAI_4o:
      return 'GPT-4o';
    case AIModel.OpenAI_4_1_Mini:
      return 'GPT-4.1 Mini';
    case AIModel.OpenAI_4_1:
      return 'GPT-4.1';
    case AIModel.OpenAI_5_Mini:
      return 'GPT-5 Mini';
    case AIModel.OpenAI_5:
      return 'GPT-5';
    case AIModel.OpenAI_5_1:
      return 'GPT-5.1';
    case AIModel.Snowflake:
      return 'Snowflake';
    default:
      return model;
  }
};
export type AIAutoAnswerSettings = {
  [SettingOption.DocumentLibrary]: string[];
  [SettingOption.OverwriteMetric]: boolean;
  [SettingOption.AIAgentMode]: boolean;
  [SettingOption.AIAgentModeOptions]: {
    model?: AIModel;
  };
};

export interface PostAiAutoAnswerSurveyParams extends GetAIAutoAnswerSurveyParams {
  settings: AIAutoAnswerSettings;
}

const UTRV_ASSISTANT_TAG = 'ai-utrv-assistant';
const AUTO_ANSWER_SURVEY_TAG = 'ai-auto-answer-survey';
const DOCUMENT_SCAN_TAG = 'ai-document-scan';
const TAGS = [UTRV_ASSISTANT_TAG, AUTO_ANSWER_SURVEY_TAG, DOCUMENT_SCAN_TAG];

export const aiApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: TAGS,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAIUtrvAssistant: builder.query<AIUtrvSuggestion, GetAIUtrvAssistantParams>({
        transformResponse,
        query: ({ initiativeId, utrvId, additionalContext }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/utrv-assistant/${utrvId}`,
            method: 'post',
            data: additionalContext,
          };
        },
        providesTags: (_result, _error, agrs) => [{ type: UTRV_ASSISTANT_TAG, id: agrs.utrvId }],
      }),
      getAIUtrvFurtherNotesDraft: builder.mutation<AIResponse, GetAIUtrvFurtherNotesDraftParams>({
        transformResponse,
        query: ({ initiativeId, ...data }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/generate-draft/further-notes`,
            method: 'post',
            data,
          };
        },
      }),
      getAIUtrvFurtherNotesDraftRefine: builder.mutation<AIResponse, GetAIUtrvFurtherNotesDraftRefineParams>({
        transformResponse,
        query: ({ initiativeId, ...requestData }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/generate-draft/further-notes/refine`,
            method: 'post',
            data: requestData,
          };
        },
      }),
      getAIAutoAnswerSurvey: builder.query<{ job: AIAutoAnswerSurveyJobPlain }, GetAIAutoAnswerSurveyParams>({
        transformResponse,
        query: ({ initiativeId, surveyId }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/survey/${surveyId}/auto-answer`,
            method: 'get',
          };
        },
        providesTags: (_result, _error, arg) => [{ type: AUTO_ANSWER_SURVEY_TAG, id: arg.surveyId }],
      }),
      aiAutoAnswerSurvey: builder.mutation<CreatedJob, PostAiAutoAnswerSurveyParams>({
        transformResponse,
        query: ({ initiativeId, surveyId, settings }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/survey/${surveyId}/auto-answer`,
            method: 'post',
            data: settings,
          };
        },
        invalidatesTags: (_result, _error, arg) => [{ type: AUTO_ANSWER_SURVEY_TAG, id: arg.surveyId }],
      }),
      aiAutoAnswerSurveyPause: builder.mutation<CreatedJob, GetAIAutoAnswerSurveyParams>({
        transformResponse,
        query: ({ initiativeId, surveyId }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/survey/${surveyId}/auto-answer/pause`,
            method: 'post',
            data: { action: 'pause' },
          };
        },
        invalidatesTags: (_result, _error, arg) => [{ type: AUTO_ANSWER_SURVEY_TAG, id: arg.surveyId }],
      }),
      aiAutoAnswerSurveyResume: builder.mutation<CreatedJob, GetAIAutoAnswerSurveyParams>({
        transformResponse,
        query: ({ initiativeId, surveyId }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/survey/${surveyId}/auto-answer/resume`,
            method: 'post',
            data: { action: 'resume' },
          };
        },
        invalidatesTags: (_result, _error, arg) => [{ type: AUTO_ANSWER_SURVEY_TAG, id: arg.surveyId }],
      }),
      aiAutoAnswerSurveyAbort: builder.mutation<CreatedJob, GetAIAutoAnswerSurveyParams>({
        transformResponse,
        query: ({ initiativeId, surveyId }) => {
          return {
            url: `/initiatives/${initiativeId}/ai/survey/${surveyId}/auto-answer/abort`,
            method: 'post',
            data: { action: 'abort' },
          };
        },
        invalidatesTags: (_result, _error, arg) => [{ type: AUTO_ANSWER_SURVEY_TAG, id: arg.surveyId }],
      }),
      getAIDocumentScan: builder.query<{ job: Pick<BackgroundJob, '_id' | 'status'> | null }, string>({
        transformResponse,
        query: (initiativeId) => {
          return {
            url: `/initiatives/${initiativeId}/ai/document-scan`,
            method: 'get',
          };
        },
        providesTags: (_result, _error, arg) => [{ type: DOCUMENT_SCAN_TAG, id: arg }],
      }),
    }),
  });

export const {
  useGetAIUtrvAssistantQuery,
  useLazyGetAIUtrvAssistantQuery,
  useGetAIUtrvFurtherNotesDraftMutation,
  useGetAIUtrvFurtherNotesDraftRefineMutation,
  useGetAIAutoAnswerSurveyQuery,
  useAiAutoAnswerSurveyMutation,
  useAiAutoAnswerSurveyPauseMutation,
  useAiAutoAnswerSurveyResumeMutation,
  useAiAutoAnswerSurveyAbortMutation,
  useGetAIDocumentScanQuery,
} = aiApi;
